import React, { useEffect, useState, useCallback } from "react";
import ReceiptModal from "./ReceiptModal";
import * as XLSX from "xlsx";
import Receipt from "./Receipt";
import PatientEditModal from "./PatientEditModal";
import Select from "react-select";
import { customFetch } from '../apiClient';

// ── Summary Card Component ──────────────────────────────────────
const SummaryCard = ({ icon, label, value, accent }) => (
  <div className="sum-card" style={{ "--accent": accent }}>
    <div className="sum-icon" style={{color: accent}}>{icon}</div>
    <div className="sum-body">
      <div className="sum-val" style={{color: accent}}>₹{Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
      <div className="sum-label">{label}</div>
    </div>
    <div className="sum-glow" />
  </div>
);

const IpdList = () => {
  const [ipdList, setIpdList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [useDateFilter, setUseDateFilter] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("");

  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptPopupData, setReceiptPopupData] = useState(null);

  // EDIT MODAL STATES
  const [editPatient, setEditPatient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [companyDetails, setCompanyDetails] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);

  // PAGINATION STATES
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // 🚀 NEW: State for Financial Summary
  const [summaryData, setSummaryData] = useState(null);

  const token = sessionStorage.getItem("token");

  const searchOptions = [
    { value: "", label: "All Fields" },
    { value: "name", label: "Name" },
    { value: "mobile", label: "Mobile No" },
    { value: "uhid", label: "UHID No" },
    { value: "srno", label: "Last 4 Digits" },
    { value: "id", label: "ID" }
  ];

  // Export to Excel with "Save As" Dialog
  useEffect(() => {
    const handleExportEvent = async () => {
      if (!ipdList || ipdList.length === 0) {
        alert("No records found to export!");
        return;
      }

      const exportData = ipdList.map((item, index) => ({
        "Sr No": index + 1,
        "IPD ID": item.ipD_SRNO || item.ipD_ID,
        "UHID": item.uhiD_No || item.uhidNo,
        "Patient Name": item.name,
        "Age": item.age,
        "Gender": item.gender,
        "Doctor": item.doctorName,
        "Department": item.departmentName,
        "IPD Date": formatDateToDDMMYYYY(item.ipD_Date),
        "Mobile": item.mobileNo,
        "Address": item.address,
        "Status": item.diS_STS ? "Discharged" : "Active"
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "IPD_Patients");

      const defaultFileName = `IPD_List_${new Date().toISOString().split("T")[0]}.xlsx`;

      try {
        if (window.showSaveFilePicker) {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: defaultFileName,
            types: [{
              description: 'Excel Workbook',
              accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
            }],
          });
          const writable = await fileHandle.createWritable();
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          await writable.write(excelBuffer);
          await writable.close();
        } else {
          XLSX.writeFile(workbook, defaultFileName);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Export Error:", error);
          alert("Error saving file!");
        }
      }
    };

    window.addEventListener("exportData", handleExportEvent);
    return () => window.removeEventListener("exportData", handleExportEvent);
  }, [ipdList]);

  const fetchIpdList = async (page = currentPage, rows = rowsPerPage) => {
    try {
      setLoading(true);
      setError("");
      
      let url = `/Ipds/search?page=${page}&pageSize=${rows}&useDateFilter=${useDateFilter}&`;
      
      if (useDateFilter) {
        if (fromDate) url += `from=${fromDate}&`;
        if (toDate) url += `to=${toDate}&`;
      }
      
      if (searchText) {
        url += `search=${encodeURIComponent(searchText)}&`;
        if (searchType) {
          url += `searchType=${searchType}&`;
        }
      }

      if (selectedDepartment) url += `departmentId=${selectedDepartment.value}&`;
      if (selectedDoctor) url += `doctorId=${selectedDoctor.value}&`;

      url = url.replace(/[&?]$/, "");

      const res = await customFetch(url);
      if (!res.ok) throw new Error("Failed to fetch IPD data");
      const data = await res.json();
      
      setIpdList(data.data || data || []);
      setTotalRecords(data.totalRecords || data.length || 0);

      // 🚀 NEW: Set Summary Data from API
      if (data.summary) {
          setSummaryData(data.summary);
      }

      setCurrentPage(page);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchTypeChange = (selectedOption) => {
    setSearchType(selectedOption ? selectedOption.value : "");
    handleFilterChange();
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

const fetchDepartments = useCallback(async () => {
  try {
    const res = await customFetch(`/Departments`);
    if (!res.ok) throw new Error("Failed to load departments");
    const data = await res.json();
    setDepartments(data ? data.map(d => ({ value: d.srno, label: d.departmentName, isActive: d.isActive })) : []); // 👈 YAHAN BHI isActive ADD KIYA HAI
  } catch (err) {
    console.error(err.message);
  }
}, [token]);

  const fetchDoctors = useCallback(async () => {
    try {
     const res = await customFetch(`/Employees/doctors`);
      if (!res.ok) throw new Error("Failed to load doctors");
      const data = await res.json();
      setDoctors(data ? data.map(d => ({ value: d.id, label: d.name })) : []);
    } catch (err) {
      console.error(err.message);
    }
  }, [token]);

  useEffect(() => {
    fetchDepartments();
    fetchDoctors();
  }, [fetchDepartments, fetchDoctors]);

  useEffect(() => {
    fetchIpdList(1, rowsPerPage || 10);
  }, [fromDate, toDate, searchText, searchType, useDateFilter, selectedDepartment, selectedDoctor]); 

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    const loginId = sessionStorage.getItem("LoginID");
    setLoggedInUser(loginId);

   if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to load company details");
          return res.json();
        })
        .then((data) => setCompanyDetails(data))
        .catch(console.error);
    }
  }, [token]);

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    handleFilterChange();
  };
  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    handleFilterChange();
  };
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    handleFilterChange();
  };

  const handleRowsPerPageChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === '') {
      setRowsPerPage('');
      setCurrentPage(1);
      return;
    }
    let value = parseInt(rawValue);
    if (isNaN(value) || value < 1) value = 10;
    if (value > 100) value = 100;
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleRowsPerPageBlur = (e) => {
    if (e.target.value === '' || !rowsPerPage) {
      setRowsPerPage(10);
    }
  };

  const handlePageChange = (page) => {
    fetchIpdList(page, rowsPerPage || 10);
  };

  const effectiveRows = rowsPerPage || 10;
  const totalPages = Math.ceil(totalRecords / effectiveRows);

  const handleEditClick = (patient) => {
    setEditPatient(patient);
    setShowEditModal(true);
  };

  const handlePreview = (record) => {
    setReceiptData(record);
    setShowReceipt(true);
  };

  const handleOpenReceiptPopup = (record) => {
    setReceiptPopupData(record);
    setShowReceiptPopup(true);
  };

  const handleDelete = (id) => {
   if (window.confirm("Are you sure you want to delete this record?")) {
      customFetch(`/Ipds/${id}`, {
        method: "DELETE"
      })
        .then((res) => {
          if (res.ok) {
            alert("Record deleted successfully");
            fetchIpdList(currentPage, rowsPerPage || 10);
          } else {
            alert("Failed to delete record");
          }
        })
        .catch(() => alert("Network error while deleting"));
    }
  };

  function formatDateToDDMMYYYY(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // SCALED DOWN COMPACT REACT SELECT STYLES
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '30px', 
      fontSize: '11px', 
      borderRadius: '6px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 0, 0.05)' : 'none',
      '&:hover': {
        borderColor: 'var(--theme-color, #0088cc)'
      }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '6px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e0e6ed',
      fontSize: '11px' 
    }),
    option: (base, state) => ({
      ...base,
      padding: '6px 10px',
      backgroundColor: state.isSelected 
        ? 'var(--theme-color, #0088cc)' 
        : state.isFocused 
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
          : 'white',
      color: state.isSelected ? 'white' : '#2c3e50',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'var(--theme-color, #0088cc)',
      },
    }),
  };

  const renderPagination = () => {
    if (!effectiveRows || totalRecords === 0) return null;
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (currentPage > 1) {
      pageNumbers.push(
        <button key="prev" className="pagination-btn pagination-btn-nav" onClick={() => handlePageChange(currentPage - 1)}>
          <i className="bi bi-chevron-left"></i> Prev
        </button>
      );
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button key={i} className={`pagination-btn ${currentPage === i ? 'active' : ''}`} onClick={() => handlePageChange(i)}>
          {i}
        </button>
      );
    }
    if (currentPage < totalPages) {
      pageNumbers.push(
        <button key="next" className="pagination-btn pagination-btn-nav" onClick={() => handlePageChange(currentPage + 1)}>
          Next <i className="bi bi-chevron-right"></i>
        </button>
      );
    }
    return pageNumbers;
  };

  return (
    <div className="ipd-list-container compact-ui">
      <div className="ipd-list-card">
        {/* Header */}
        <div className="ipd-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-hospital-fill"></i>
            </div>
            <div className="header-text">
              <h5>IPD Patient List & Financials</h5>
              <p>View inpatient records and track revenue</p>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <i className="bi bi-people-fill"></i>
              <div className="stat-info">
                <span className="stat-value">{totalRecords}</span>
                <span className="stat-label">Total Patients</span>
              </div>
            </div>
          </div>
        </div>

        {/* Alert */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        <div className="ipd-body">
          {/* Filters Section */}
          <div className="filters-section">
            <div className="section-header">
              <i className="bi bi-funnel"></i>
              <span>Filters & Search</span>
            </div>
            <div className="filters-content">
              
              <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input 
                  type="checkbox" 
                  id="dateFilterCheck"
                  checked={useDateFilter} 
                  onChange={(e) => {
                    setUseDateFilter(e.target.checked);
                    handleFilterChange();
                  }} 
                  style={{ width: '13px', height: '13px', cursor: 'pointer', margin: 0 }}
                />
                <label htmlFor="dateFilterCheck" style={{ fontSize: '11px', fontWeight: '600', color: '#2c3e50', cursor: 'pointer', margin: 0 }}>
                  Apply Date Filter
                </label>
              </div>

              <div className="filter-row">
                <div className="filter-group">
                  <label><i className="bi bi-calendar-event"></i> From Date</label>
                  <input 
                    type="date" 
                    className="form-control-custom" 
                    value={fromDate} 
                    onChange={handleFromDateChange} 
                    disabled={!useDateFilter} 
                    style={{ opacity: !useDateFilter ? 0.5 : 1 }}
                  />
                </div>

                <div className="filter-group">
                  <label><i className="bi bi-calendar-check"></i> To Date</label>
                  <input 
                    type="date" 
                    className="form-control-custom" 
                    value={toDate} 
                    onChange={handleToDateChange} 
                    disabled={!useDateFilter} 
                    style={{ opacity: !useDateFilter ? 0.5 : 1 }}
                  />
                </div>

                <div className="filter-group" style={{ minWidth: '130px' }}>
                  <label><i className="bi bi-building"></i> Department</label>
              <Select 
                    options={departments.filter(d => d.isActive === true)} 
                    value={selectedDepartment}
                    onChange={(option) => { setSelectedDepartment(option); handleFilterChange(); }}
                    styles={selectStyles}
                    placeholder="All Depts"
                    isClearable={true}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="filter-group" style={{ minWidth: '130px' }}>
                  <label><i className="bi bi-person-badge"></i> Doctor</label>
                  <Select 
                    options={doctors}
                    value={selectedDoctor}
                    onChange={(option) => { setSelectedDoctor(option); handleFilterChange(); }}
                    styles={selectStyles}
                    placeholder="All Doctors"
                    isClearable={true}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="filter-group" style={{ minWidth: '120px' }}>
                  <label><i className="bi bi-search"></i> Search By</label>
                  <Select 
                    options={searchOptions}
                    value={searchOptions.find(opt => opt.value === searchType)}
                    onChange={handleSearchTypeChange}
                    styles={selectStyles}
                    placeholder="All Fields"
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="filter-group flex-2">
                  <label>
                    <i className="bi bi-filter"></i> Search {searchType ? `by ${searchOptions.find(o => o.value === searchType)?.label}` : ''}
                  </label>
                  <input
                    type="text"
                    className="form-control-custom"
                    value={searchText}
                    onChange={handleSearchChange}
                    placeholder={searchType ? `Enter ${searchOptions.find(o => o.value === searchType)?.label}...` : "Search all fields..."}
                  />
                </div>

                <div className="filter-group" style={{ width: '60px', flexGrow: 0 }}>
                  <label><i className="bi bi-table"></i> Rows</label>
                  <input
                    type="number"
                    className="form-control-custom"
                    value={rowsPerPage}
                    placeholder="10"
                    min="1" max="100"
                    onChange={handleRowsPerPageChange}
                    onBlur={handleRowsPerPageBlur}
                  />
                </div>

                <div className="filter-group" style={{ minWidth: '80px', flexGrow: 0 }}>
                  <label>&nbsp;</label>
                  <button className="btn-refresh" onClick={() => fetchIpdList(1, rowsPerPage || 10)} disabled={loading}>
                    <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i> Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 🚀 NEW: Financial Summary Cards */}
          {summaryData && (
            <div className="cards-grid">
              <SummaryCard icon={<i className="bi bi-cash-stack"></i>} label="Total Consult Fee" value={summaryData.totalConsultFee} accent="#0ea5e9" />
              <SummaryCard icon={<i className="bi bi-tags"></i>} label="Total Discount" value={summaryData.totalDiscount} accent="#7c3aed" />
              <SummaryCard icon={<i className="bi bi-calculator"></i>} label="Total Payable" value={summaryData.totalPayable} accent="#059669" />
              <SummaryCard icon={<i className="bi bi-wallet2"></i>} label="Total Received" value={summaryData.totalReceived} accent="#d97706" />
              <SummaryCard icon={<i className="bi bi-exclamation-circle"></i>} label="Total Dues" value={summaryData.totalDue} accent="#dc2626" />
            </div>
          )}

          {/* Records Info */}
          <div className="records-info">
            <div className="info-left">
              <i className="bi bi-info-circle"></i>
              <span>Showing {((currentPage - 1) * effectiveRows) + 1} to {Math.min(currentPage * effectiveRows, totalRecords)} of {totalRecords} entries</span>
            </div>
            <div className="info-right">
              {totalPages > 0 && (
                <span className="page-info">
                  <i className="bi bi-file-earmark-text"></i> Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="table-container">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading IPD records...</p>
              </div>
            ) : ipdList.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox"></i>
                <h6>No IPD Records Found</h6>
                <p>No patients match your search criteria</p>
              </div>
            ) : (
              <table className="ipd-table">
                <thead>
                  <tr>
                    <th><i className="bi bi-prescription"></i> Rx</th>
                    <th><i className="bi bi-file-earmark-medical"></i> Slip</th>
                    <th><i className="bi bi-hash"></i> ID</th>
                    <th><i className="bi bi-card-heading"></i> UHID</th>
                    <th><i className="bi bi-person"></i> Name</th>
                    <th><i className="bi bi-gender-ambiguous"></i> Age/Gen</th>
                    <th><i className="bi bi-person-badge"></i> Doctor</th>
                    <th><i className="bi bi-building"></i> Dept</th>
                    <th><i className="bi bi-calendar"></i> Date</th>
                    <th><i className="bi bi-geo-alt"></i> Address</th>
                    <th><i className="bi bi-phone"></i> Mobile</th>
                    <th className="text-center"><i className="bi bi-tools"></i> Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ipdList.map((i) => (
                    <tr key={i.ipD_ID}>
                      <td className="action-cell">
                        <button className="btn-icon btn-prescription" onClick={() => handleOpenReceiptPopup(i)} title="View Prescription">
                          <i className="bi bi-eye-fill"></i>
                        </button>
                      </td>
                      <td className="action-cell">
                        <button className="btn-icon btn-regslip" onClick={() => handlePreview(i)} title="View RegSlip">
                          <i className="bi bi-file-text"></i>
                        </button>
                      </td>
                      <td title={i.ipD_SRNO}><span className="id-badge">{i.ipD_SRNO}</span></td>
                      <td title={i.uhiD_No}><span className="uhid-badge">{i.uhiD_No}</span></td>
                      <td title={i.name}>
                        <div className="patient-name">
                          <i className="bi bi-person-circle"></i> {i.name}
                        </div>
                      </td>
                      <td title={`${i.age} / ${i.gender}`}><span className="age-gender">{i.age} / {i.gender?.charAt(0)}</span></td>
                      <td title={i.doctorName}>
                        <div className="doctor-name">
                          <i className="bi bi-person-fill-check"></i> {i.doctorName}
                        </div>
                      </td>
                      <td title={i.departmentName}>{i.departmentName}</td>
                      <td><span className="date-badge">{formatDateToDDMMYYYY(i.ipD_Date)}</span></td>
                      <td className="address-cell" title={i.address}>{i.address}</td>
                      <td>
                        <span className="mobile-badge"><i className="bi bi-telephone"></i> {i.mobileNo}</span>
                      </td>
                      <td className="action-cell">
                        <div className="action-buttons">
                          <button className="btn-icon btn-edit" onClick={() => handleEditClick(i)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                          <button className="btn-icon btn-delete" onClick={() => handleDelete(i.ipD_ID)} title="Delete"><i className="bi bi-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-section">
              {renderPagination()}
            </div>
          )}
        </div>
      </div>

      {companyDetails && (
        <ReceiptModal show={showReceipt} data={receiptData} companyDetails={companyDetails} loggedInUser={loggedInUser} onClose={() => setShowReceipt(false)} onPrint={() => window.print()} />
      )}

      {companyDetails && showReceiptPopup && (
        <Receipt show={showReceiptPopup} data={receiptPopupData} companyDetails={companyDetails} loggedInUser={loggedInUser} onClose={() => setShowReceiptPopup(false)} />
      )}

      {showEditModal && editPatient && (
        <PatientEditModal entityType="IPD" patientData={editPatient} departments={departments} doctors={doctors} onClose={() => setShowEditModal(false)} onUpdate={() => { fetchIpdList(currentPage, rowsPerPage || 10); setShowEditModal(false); }} />
      )}

      {/* COMPACT UI SCALED DOWN CSS */}
      <style jsx>{`
        .compact-ui { font-family: system-ui, -apple-system, sans-serif; }
        
        .ipd-list-container { padding: 0; }
        .ipd-list-card { background: white; border-radius: 8px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05); border: 1px solid #e0e6ed; overflow: hidden; }

        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .slide-in { animation: slideIn 0.2s ease; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spinning { animation: spin 1s linear infinite; }

        /* Header */
        .ipd-header {
          background: var(--theme-color, #0088cc); 
          padding: 8px 16px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #00d4aa;
        }
        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon {
          width: 32px; height: 32px; background: rgba(255, 255, 255, 0.2); border-radius: 6px;
          display: flex; align-items: center; justify-content: center; font-size: 16px; color: white;
        }
        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }
        .header-stats { display: flex; gap: 10px; }
        .stat-item {
          background: rgba(255, 255, 255, 0.2); padding: 4px 10px; border-radius: 6px; display: flex; align-items: center; gap: 6px;
        }
        .stat-item i { font-size: 16px; color: white; }
        .stat-info { display: flex; flex-direction: column; }
        .stat-value { color: white; font-size: 14px; font-weight: 700; line-height: 1; }
        .stat-label { color: rgba(255, 255, 255, 0.85); font-size: 9px; }

        /* Alert */
        .alert-custom { margin: 10px 16px 0; padding: 8px 12px; border-radius: 6px; display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 500; position: relative; }
        .alert-danger { background: #fee; color: #c33; border: 1px solid #fcc; }
        .alert-custom i { font-size: 13px; }
        .alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; padding: 4px; color: inherit; opacity: 0.6; }

        .ipd-body { padding: 12px 16px; }

        /* Filters Section */
        .filters-section { background: #ffffff; border: 1px solid #e0e6ed; border-radius: 6px; margin-bottom: 10px; overflow: visible;}
        .section-header {
          background: #f0f4f8; padding: 6px 12px; display: flex; align-items: center; gap: 6px; border-bottom: 2px solid var(--theme-color, #0088cc);
        }
        .section-header i { font-size: 13px; color: var(--theme-color, #0088cc); }
        .section-header span { font-size: 12px; font-weight: 600; color: #2c3e50; }
        .filters-content { padding: 10px; }
        
        .filter-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: end; }
        .filter-group { display: flex; flex-direction: column; flex-grow: 1; }
        .filter-group.flex-2 { flex-grow: 2; min-width: 180px; }
        .filter-group label { font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 3px; }
        .filter-group label i { color: var(--theme-color, #0088cc); font-size: 10px; margin-right: 2px; }

        .form-control-custom {
          padding: 4px 8px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 11px;
          color: #2c3e50; width: 100%; height: 30px; outline: none; transition: 0.2s;
        }
        .form-control-custom:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,0,0,0.05); }

        .btn-refresh {
          padding: 0 10px; border-radius: 6px; border: none; background: var(--theme-color, #0088cc); color: white;
          font-size: 11px; font-weight: 600; cursor: pointer; height: 30px; display: flex; align-items: center; justify-content: center; gap: 4px; width: 100%;
        }
        .btn-refresh:hover:not(:disabled) { transform: translateY(-1px); opacity: 0.9; }
        .btn-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

        /* 🚀 NEW CSS FOR SUMMARY CARDS */
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 12px;
          margin-bottom: 12px;
        }
        .sum-card {
          position: relative;
          overflow: hidden;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .sum-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        .sum-glow {
          position: absolute;
          bottom: -20px;
          right: -20px;
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: var(--accent);
          opacity: 0.08;
          filter: blur(20px);
        }
        .sum-icon {
          font-size: 24px;
          line-height: 1;
        }
        .sum-body { display: flex; flex-direction: column; }
        .sum-val {
          font-size: 16px;
          font-weight: 800;
          font-family: 'DM Mono', monospace;
          line-height: 1.2;
        }
        .sum-label {
          font-size: 10px;
          font-weight: 600;
          color: #64748b;
          margin-top: 2px;
          text-transform: uppercase;
        }

        /* Records Info */
        .records-info {
          display: flex; justify-content: space-between; align-items: center; padding: 6px 10px;
          background: #f0f4f8; border-radius: 6px; margin-bottom: 10px; border: 1px solid #e0e6ed; font-size: 11px;
        }
        .info-left { display: flex; align-items: center; gap: 6px; color: #2c3e50; font-weight: 500; }
        .info-left i { color: var(--theme-color, #0088cc); }
        .page-info {
          background: white; padding: 3px 10px; border-radius: 50px; display: flex; align-items: center; gap: 4px;
          border: 1px solid var(--theme-color, #0088cc); font-size: 10px; font-weight: 600; color: var(--theme-color, #0088cc);
        }

        /* Table */
        .table-container { background: white; border: 1px solid #e0e6ed; border-radius: 6px; width: 100%; overflow: hidden; }
        .ipd-table { width: 100%; border-collapse: collapse; font-size: 10.5px; table-layout: fixed; }
        .ipd-table thead { background: var(--theme-color, #0088cc); }
        .ipd-table th {
          padding: 6px 4px; text-align: left; font-weight: 600; color: white;
          border-bottom: 2px solid #00d4aa; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 10px;
        }

        .ipd-table th:nth-child(1) { width: 3.5%; text-align: center; } 
        .ipd-table th:nth-child(2) { width: 3.5%; text-align: center; } 
        .ipd-table th:nth-child(3) { width: 6%; }  
        .ipd-table th:nth-child(4) { width: 11%; } 
        .ipd-table th:nth-child(5) { width: 15%; } 
        .ipd-table th:nth-child(6) { width: 7%; }  
        .ipd-table th:nth-child(7) { width: 12%; } 
        .ipd-table th:nth-child(8) { width: 10%; } 
        .ipd-table th:nth-child(9) { width: 8%; }  
        .ipd-table th:nth-child(10) { width: 14%; } 
        .ipd-table th:nth-child(11) { width: 10%; } 
        .ipd-table th:nth-child(12) { width: 6%; text-align: center; } 

        .ipd-table tbody tr { border-bottom: 1px solid #e0e6ed; }
        .ipd-table tbody tr:hover { background: #f8f9fa; }
        .ipd-table td { padding: 4px; color: #2c3e50; vertical-align: middle; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* Cell Specifics */
        .id-badge, .uhid-badge { background: #e7f3ff; color: var(--theme-color, #0088cc); padding: 2px 4px; border-radius: 4px; font-weight: 600; font-size: 9px; display: inline-block; }
        .patient-name, .doctor-name { font-weight: 500; display: flex; align-items: center; gap: 4px; }
        .patient-name i, .doctor-name i { font-size: 11px; color: var(--theme-color, #0088cc); flex-shrink: 0; }
        .address-cell { color: #555; font-size: 10px; }
        .mobile-badge { display: inline-flex; align-items: center; gap: 3px; color: #2c3e50; }
        .mobile-badge i { color: var(--theme-color, #0088cc); font-size: 10px;}
        .date-badge { background: #f8f9fa; padding: 2px 6px; border-radius: 4px; font-weight: 500; border: 1px solid #e0e6ed; font-size: 10px; }
        .age-gender { font-size: 10px; }

        /* Buttons */
        .btn-icon {
          width: 22px; height: 22px; border-radius: 4px; border: none; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; font-size: 11px; margin: 0 1px;
        }
        .btn-prescription { background: #28a745; color: white; }
        .btn-regslip { background: var(--theme-color, #0088cc); color: white; }
        .btn-edit { background: #e7f3ff; color: var(--theme-color, #0088cc); }
        .btn-delete { background: #fee; color: #dc3545; }
        .action-buttons { display: flex; justify-content: center; }
        .action-cell { text-align: center; }

        /* Pagination */
        .pagination-section { display: flex; justify-content: center; gap: 4px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e6ed; }
        .pagination-btn { padding: 3px 8px; border-radius: 4px; border: 1px solid #cbd5e0; background: white; color: #2c3e50; font-size: 10px; font-weight: 600; cursor: pointer; }
        .pagination-btn:hover { border-color: var(--theme-color, #0088cc); color: var(--theme-color, #0088cc); transform: translateY(-1px); }
        .pagination-btn.active { background: var(--theme-color, #0088cc); color: white; border-color: var(--theme-color, #0088cc); }
        
        .loading-state, .empty-state { text-align: center; padding: 30px 20px; }
        .empty-state i { font-size: 36px; color: #cbd5e0; margin-bottom: 10px; display: block;}
        .empty-state h6 { color: #2c3e50; font-weight: 600; margin-bottom: 5px; font-size: 13px;}
        .empty-state p { font-size: 11px; margin: 0; }

        @media (max-width: 1200px) { .cards-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 1024px) {
          .table-container { overflow-x: auto; }
          .ipd-table { min-width: 1000px; }
        }
        @media (max-width: 768px) {
          .cards-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 480px) {
          .cards-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default IpdList;